import { useLocalSearchParams } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { goBack } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { OrderPlaced, orderPlacedAt } from '@/components/social/order/OrderPlaced';
import { OrderProductStep } from '@/components/social/order/OrderProductStep';
import { OrderReviewStep } from '@/components/social/order/OrderReviewStep';
import { OrderShell } from '@/components/social/order/OrderShell';
import { OrderShippingStep } from '@/components/social/order/OrderShippingStep';
import { Stack } from '@/components/Stack';
import { Text } from '@/components/Text';
import { colors } from '@/constants/theme';
import { useDealCta } from '@/hooks/useDealCta';
import { playbookStep } from '@/lib/socialProgress';
import { EMPTY_SHIPPING, shippingErrors, tidyShipping, type ShippingErrors } from '@/lib/voltrShipping';
import { dealsStore } from '@/stores/deals.store';
import { voltrStore } from '@/stores/voltr.store';
import type { OrderedProduct, VoltrShipping } from '@/types/voltr';

type Step = 'product' | 'shipping' | 'review';
const PREVIOUS: Record<Step, Step | null> = { product: null, shipping: 'product', review: 'shipping' };
const BACK_LABEL: Record<Step, string> = { product: 'View Deal', shipping: 'Select Product', review: 'Shipping Info' };

/**
 * MarketPryce Social (Voltr): order the brand's product in three steps
 * (Figma "Order 1-4"): pick it, say where it ships, read both back, then
 * "Order placed!". Every rule (who can order, why not, what's in stock)
 * comes from GET /deals/:slug/social. Nothing is sent before Place Order.
 * The address goes straight to the server, which passes it to Voltr and
 * never stores it; here it lives in this screen's state and nowhere else.
 */
const VoltrOrderScreen = observer(() => {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const [step, setStep] = useState<Step>('product');
  const [productId, setProductId] = useState<string | null>(null);
  const [shipping, setShipping] = useState<VoltrShipping>(EMPTY_SHIPPING);
  const [errors, setErrors] = useState<ShippingErrors>({});
  // Set by this visit's order: the address shows on the confirmation once, from here.
  const [justPlaced, setJustPlaced] = useState<{ product: OrderedProduct; shipTo: VoltrShipping } | null>(null);
  const submitLock = useRef(false);

  const load = useCallback(() => {
    if (slug) void voltrStore.load(slug);
  }, [slug]);

  useEffect(() => {
    void dealsStore.ensureLoaded();
    load();
  }, [load]);

  const deal = slug ? dealsStore.bySlug(slug) : undefined;
  const { readBrief, ctaError } = useDealCta(deal, deal ? dealsStore.actionFor(deal.id) : undefined);
  const social = voltrStore.socialSlug === slug ? voltrStore.social : null;
  const brand = social?.brand ?? null;
  const action = social?.action ?? null;
  const product = brand?.products.find((p) => p.id === productId) ?? null;
  const ordered = justPlaced?.product ?? social?.ordered_product ?? null;
  const playbook = deal && playbookStep(deal);

  const setField = (field: keyof VoltrShipping, value: string) => {
    setShipping((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toReview = () => {
    const missing = shippingErrors(shipping);
    setErrors(missing);
    if (Object.keys(missing).length === 0) setStep('review');
  };

  const placeOrder = async () => {
    // Held from the tap until the order settles: a second tap must not send a second order.
    if (!action || !product || submitLock.current) return;
    submitLock.current = true;
    try {
      const shipTo = tidyShipping(shipping);
      if (await voltrStore.order(action.id, product.id, shipTo)) setJustPlaced({ product, shipTo });
    } finally {
      submitLock.current = false;
    }
  };

  if (ordered) {
    return (
      <OrderPlaced
        product={ordered}
        placedAt={orderPlacedAt(social?.action)}
        shipTo={justPlaced?.shipTo}
        onReadPlaybook={playbook ? () => void readBrief(playbook) : undefined}
        playbookError={ctaError}
        onDone={() => goBack(`/deals/${slug}`)}
      />
    );
  }

  // Nothing loaded and nothing failed yet = the load is on its way (the first
  // render runs before the effect that starts it).
  if (!social && !voltrStore.loadError) {
    return (
      <OrderShell testID="voltr-order-loading">
        <ActivityIndicator color={colors.primary} />
      </OrderShell>
    );
  }

  if (!social || !brand) {
    return (
      <OrderShell testID="voltr-order-error">
        <ErrorState bare={!!social} body={voltrStore.loadError ?? undefined} onRetry={load} />
      </OrderShell>
    );
  }

  // Why there is nothing to order yet, when that's the case. A brand still
  // setting up on Voltr's side gets no timer: the user asks again when they want to.
  const blocked = !action ? (
    <EmptyState title="Apply first" body="Once a coach approves you, you can pick your product here." testID="voltr-order-no-action" />
  ) : !brand.ready ? (
    <Stack gap="md" testID="voltr-order-not-ready">
      <Text color="textMuted">{brand.name} is still getting its products ready. Check again in a minute.</Text>
      <Button label="Check again" variant="outline" fullWidth loading={voltrStore.loading} onPress={load} />
    </Stack>
  ) : brand.products.length === 0 ? (
    <EmptyState
      title="Nothing to pick yet"
      body={brand.unavailable_reason ?? `${brand.name} has no products listed right now.`}
      testID="voltr-order-empty"
    />
  ) : null;
  if (blocked) return <OrderShell testID="voltr-order">{blocked}</OrderShell>;

  const previous = PREVIOUS[step];
  const onBack = previous ? () => setStep(previous) : undefined;
  const backLabel = BACK_LABEL[step];

  if (step === 'review' && product) {
    return (
      <OrderShell
        onBack={onBack}
        backLabel={backLabel}
        progress={{ step: 3, label: 'Review' }}
        footer={
          <Button
            label="Place Order"
            fullWidth
            trailingIcon={ArrowRight}
            loading={voltrStore.submitting}
            onPress={() => void placeOrder()}
            testID="voltr-order-submit"
          />
        }
        testID="voltr-order"
      >
        <OrderReviewStep brandName={brand.name} product={product} shipping={tidyShipping(shipping)} error={voltrStore.submitError} />
      </OrderShell>
    );
  }

  if (step === 'shipping' && product) {
    return (
      <OrderShell
        onBack={onBack}
        backLabel={backLabel}
        progress={{ step: 2, label: 'Shipping info' }}
        footer={<Button label="Review Order" fullWidth trailingIcon={ArrowRight} onPress={toReview} testID="voltr-order-to-review" />}
        testID="voltr-order"
      >
        <OrderShippingStep shipping={shipping} errors={errors} onChange={setField} />
      </OrderShell>
    );
  }

  return (
    <OrderShell
      progress={{ step: 1, label: 'Select your product' }}
      footer={
        <Button
          label="Continue to Shipping"
          fullWidth
          trailingIcon={ArrowRight}
          disabled={!product}
          onPress={() => setStep('shipping')}
          testID="voltr-order-to-shipping"
        />
      }
      testID="voltr-order"
    >
      <OrderProductStep brand={brand} logoUrl={deal?.brand_logo_url} selectedId={productId} onSelect={setProductId} />
    </OrderShell>
  );
});

export default VoltrOrderScreen;
